import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { capitalize } from '@dhl/utils'
import { SelectableCardList, type SelectableCardData } from '~/widgets'
import {
  getPackageConstraints,
  allowReturnType,
  type CountryCode,
  type ReturnPortal,
  type TransportType,
  type ReturnType,
} from '~/model'
import { ReturnTypeDetailInfo } from '~/components'
import { insertIf } from '~/helpers'

type ItemType = SelectableCardData<ReturnType>

export const getReturnTypeItems = (
  returnPortal: ReturnPortal,
  transportType: TransportType | undefined,
  countryCode: CountryCode,
  t: TFunction,
  language: string
): [ItemType[], ReturnType | undefined] => {
  const isCcc = returnPortal.businessUnitCode === 'ccc'
  const isParcel = transportType === 'parcel'
  const isPallet = transportType === 'pallet'
  const showServicePointItem = isParcel && allowReturnType(returnPortal, 'service_point', countryCode)
  const showConsumerPickupItem = isParcel && allowReturnType(returnPortal, 'consumer_pickup', countryCode)
  let showPickupItem = (isParcel || isPallet) && allowReturnType(returnPortal, 'pickup', countryCode)
  // If both pickup and consumer_pickup are allowed, then only show consumer_pickup
  if (showConsumerPickupItem && showPickupItem) {
    showPickupItem = false
  }

  const transportTypeLabel = t(transportType || '')
  const transportTypeLabel1OrMore = t(`${transportType}1OrMore`)
  const pickDescription = t('pickupDescription', {
    // in German, nouns should be capitalized:
    packageType: language === 'de' ? capitalize(transportTypeLabel) : transportTypeLabel,
  })
  const pickDescriptionBusiness = t('pickupDescriptionBusiness', {
    packageType: transportTypeLabel1OrMore,
  })
  const items = [
    ...insertIf<ItemType>(showServicePointItem, {
      label: t('servicePointLabel'),
      description: isCcc
        ? t('servicePointDescriptionCcc')
        : t('servicePointDescription', {
            maxWeight: getPackageConstraints(isCcc, 'service_point', 'parcel').weight,
          }),
      value: 'service_point',
      iconName: 'ServicePoint',
    }),
    ...insertIf<ItemType>(showConsumerPickupItem, {
      label: t('consumerPickupLabel'),
      description: isCcc
        ? t('consumerPickupDescriptionCcc')
        : t('consumerPickupDescription', {
            maxWeight: getPackageConstraints(isCcc, 'consumer_pickup', 'parcel').weight,
          }),
      value: 'consumer_pickup',
      iconName: 'Truck',
    }),
    ...insertIf<ItemType>(showPickupItem, {
      label: t('pickupLabel'),
      description: isPallet ? pickDescriptionBusiness : pickDescription,
      value: 'pickup',
      iconName: 'BigTruck',
    }),
  ]
  const defaultValue = items.length === 1 ? items[0].value : undefined
  return [items, defaultValue]
}

type Props = {
  returnPortal: ReturnPortal
  transportType?: TransportType
  returnType?: ReturnType
  countryCode: CountryCode
  showRequiredDataLabel: boolean
  isAddressAndContactValid: boolean
  showLabelTypeInput: boolean
}

export const ReturnTypeInput = ({
  returnType,
  returnPortal,
  countryCode,
  transportType,
  showRequiredDataLabel,
  isAddressAndContactValid,
  showLabelTypeInput,
}: Props) => {
  const { t, i18n } = useTranslation()
  const [items] = getReturnTypeItems(returnPortal, transportType, countryCode, t, i18n.language)

  return (
    <>
      <SelectableCardList<ReturnType>
        fieldName="returnType"
        title={t('chooseReturnType')}
        showRequiredDataLabel={showRequiredDataLabel}
        items={items}
      />
      {!showLabelTypeInput && returnType && (
        <ReturnTypeDetailInfo returnType={returnType} enabled={isAddressAndContactValid && items.length > 0} />
      )}
    </>
  )
}
